import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Card,
  Col,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import { formatPayrollMoney } from "../../../components/finance/payroll/payrollAmountInput";
import { usePayrollSecurity } from "../../../hooks/finance/usePayrollSecurity";

const { Title, Text } = Typography;
const money = formatPayrollMoney;

const PayrollDashboard = () => {
  const navigate = useNavigate();
  const { payrollAccess } = usePayrollSecurity();
  const [data, setData] = useState({
    counts: {},
    actionQueue: [],
    upcoming: {},
    recentTotals: [],
  });
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    const response = await Utilities.fetchWithAuth(
      "GET",
      domainSettings.urls.finance.payrollDashboard,
      null,
      navigate,
    );
    if (response.ok) setData(response.data);
    else Utilities.showError(response, "Could not load payroll dashboard");
    setLoading(false);
  }, [navigate]);
  useEffect(() => {
    load();
  }, [load]);
  const counts = data.counts || {};
  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div>
        <Title level={3} style={{ marginBottom: 4 }}>
          Payroll dashboard
        </Title>
        <Text type="secondary">
          What needs attention now, what is waiting for you, and what is coming
          next.
        </Text>
      </div>
      {(counts.openBlocking > 0 || counts.openWarnings > 0) && (
        <Alert
          showIcon
          type={counts.openBlocking ? "error" : "warning"}
          message={`${counts.openBlocking || 0} blocking issue(s) and ${counts.openWarnings || 0} warning(s) need attention`}
        />
      )}
      <Row gutter={[16, 16]}>
        {[
          ["Open periods", counts.openPeriods],
          ["Draft runs", counts.draftRuns],
          ["Pending review", counts.pendingReviews],
          ["Ready to finalize", counts.approvedToFinalize],
          ["Finalized unpaid", counts.finalizedUnpaid],
          ["Partly paid", counts.partiallyPaid],
        ].map(([label, value]) => (
          <Col xs={12} lg={4} key={label}>
            <Card>
              <Statistic title={label} value={value || 0} />
            </Card>
          </Col>
        ))}
      </Row>
      <Card title="My action queue">
        <Table
          loading={loading}
          rowKey="runId"
          pagination={false}
          dataSource={data.actionQueue || []}
          onRow={(row) => ({
            onClick: () =>
              navigate("/admin/finance/payroll/runs", {
                state: { runId: row.runId },
              }),
            style: { cursor: "pointer" },
          })}
          columns={[
            { title: "Payroll", dataIndex: "periodName" },
            { title: "Type", dataIndex: "payrollType" },
            {
              title: "Status",
              dataIndex: "status",
              render: (v) => <Tag>{v}</Tag>,
            },
            {
              title: "Payment date",
              dataIndex: "paymentDate",
              render: (v) => (v ? dayjs(v).format("DD MMM YYYY") : "—"),
            },
            { title: "Next action", dataIndex: "action" },
          ]}
        />
      </Card>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Upcoming payroll dates">
            <Table
              size="small"
              pagination={false}
              rowKey="periodId"
              dataSource={data.upcoming?.payrollDates || []}
              columns={[
                { title: "Period", dataIndex: "periodName" },
                { title: "Type", dataIndex: "payrollType" },
                {
                  title: "Date",
                  dataIndex: "date",
                  render: (v) => dayjs(v).format("DD MMM YYYY"),
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Upcoming advance deductions">
            <Table
              size="small"
              pagination={false}
              rowKey="installmentId"
              dataSource={data.upcoming?.advanceInstallments || []}
              columns={[
                { title: "Employee", dataIndex: "employeeName" },
                { title: "Reference", dataIndex: "reference" },
                {
                  title: "Due",
                  dataIndex: "date",
                  render: (v) => dayjs(v).format("DD MMM YYYY"),
                },
                {
                  title: "Amount",
                  dataIndex: "amount",
                  align: "right",
                  render: money,
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
      <Card title="Recent payroll totals">
        <Table
          rowKey="runId"
          pagination={false}
          scroll={{ x: "max-content" }}
          dataSource={data.recentTotals || []}
          columns={[
            { title: "Payroll", dataIndex: "periodName" },
            { title: "Status", dataIndex: "status" },
            { title: "Employees", dataIndex: "employeeCount" },
            {
              title: "Gross",
              dataIndex: "grossPay",
              align: "right",
              render: money,
            },
            payrollAccess.tax && {
              title: "PAYE",
              dataIndex: "paye",
              align: "right",
              render: money,
            },
            payrollAccess.nssf && {
              title: "Employee NSSF",
              dataIndex: "employeeNssf",
              align: "right",
              render: money,
            },
            payrollAccess.nssf && {
              title: "Employer NSSF",
              dataIndex: "employerNssf",
              align: "right",
              render: money,
            },
            {
              title: "Net",
              dataIndex: "netPay",
              align: "right",
              render: money,
            },
            {
              title: "Employer cost",
              dataIndex: "employerCost",
              align: "right",
              render: money,
            },
          ].filter(Boolean)}
        />
      </Card>
    </Space>
  );
};
export default PayrollDashboard;
